import App from "./App";
import { SentryProvider } from "./hooks/use-sentry";
import {
  BrowserRouter as Router,
  useNavigate
} from "react-router-dom";
import { Auth0Provider, Auth0ProviderOptions } from "@auth0/auth0-react";
import { MantineProvider, Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ConfigProvider } from "antd";
import { Button } from "antd";
import {
  apiRequest,
  theme,
  AccountProvider,
  DoubleSidebarProvider,
  DatadogProvider,
  GlobalProvider
} from "@vojomo-core/shared";
import { useEffect, useState } from "react";
import packageInfo from "../package.json";
import { ToastContainer, cssTransition } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * Wraps Auth0Provider so its onRedirectCallback can use react-router's
 * `useNavigate` instead of `window.location.href`. The hard reload was
 * wiping the SDK's in-memory token cache, forcing it to re-acquire
 * tokens via silent iframe auth on every post-login transition — and
 * when that iframe auth fails (Chrome tracking protection, fresh
 * incognito, ITP), the SDK reports `isAuthenticated=false` and
 * use-account.tsx kicks off another `login()` call, producing a loop.
 */
const Auth0ProviderWithNavigate = ({
  children,
  ...providerProps
}: Auth0ProviderOptions) => {
  const navigate = useNavigate();
  return (
    <Auth0Provider
      {...providerProps}
      onRedirectCallback={(appState) => {
        const target = appState?.returnTo || "/";
        try {
          const url = new URL(target);
          if (url.origin === window.location.origin) {
            navigate(url.pathname + url.search + url.hash, { replace: true });
            return;
          }
        } catch {
          // target wasn't an absolute URL — fall through to treat it as a path
        }
        navigate(target, { replace: true });
      }}
    >
      {children}
    </Auth0Provider>
  );
};

export const AppWrapper = () => {
  const [opened, { open, close }] = useDisclosure(false);

  const [pingData, setPingData] = useState<{
    oAuthDomain: string;
    oAuthId: string;
    oAuthAudience: string;
    version: string;
  }>();

  useEffect(() => {
    const fetchData = async () => {
      const data: any = await apiRequest({
        path: "/ping?app_name=app"
      });

      setPingData(data);

      setTimeout(fetchData, 120 * 1000);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (pingData) {
      if (
        packageInfo.version.split(".")[0] !== pingData.version.split(".")[0] ||
        packageInfo.version.split(".")[1] !== pingData.version.split(".")[1]
      ) {
        setTimeout(() => {
          window.location.href = "/";
        }, 5000);
        open();
      }

      console.log("Sv: " + pingData.version);
      console.log("App: " + packageInfo.version);
    }
  }, [pingData]);

  if (!pingData) {
    return <></>;
  }

  return (
    <Router>
      <Auth0ProviderWithNavigate
        domain={pingData.oAuthDomain}
        clientId={pingData.oAuthId}
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: pingData.oAuthAudience,
          scope: "openid profile email offline_access"
        }}
        useRefreshTokens={true}
        useRefreshTokensFallback={true}
        cacheLocation="localstorage"
      >
        <GlobalProvider pingData={pingData}>
          <ConfigProvider theme={{ token: { fontFamily: "Albert Sans" } }}>
            <MantineProvider theme={theme}>
              <AccountProvider appName={"app"}>
                <SentryProvider>
                <DatadogProvider appName={"app"}>
                  <ToastContainer
                    closeButton={false}
                    hideProgressBar={true}
                    autoClose={false}
                    transition={cssTransition({
                      enter: "dummy",
                      exit: "dummy",
                      collapse: false
                    })}
                  />
                  <DoubleSidebarProvider>
                    <Modal
                      withCloseButton={false}
                      opened={opened}
                      onClose={close}
                      title={"You are using an outdated version of the app"}
                      closeOnClickOutside={false}
                    >
                      <Button
                        onClick={() => {
                          window.location.reload();
                        }}
                      >
                        Refresh
                      </Button>
                    </Modal>
                    <App />
                  </DoubleSidebarProvider>
                </DatadogProvider>
                </SentryProvider>
              </AccountProvider>
            </MantineProvider>
          </ConfigProvider>
        </GlobalProvider>
      </Auth0ProviderWithNavigate>
    </Router>
  );
};
